import { FieldProps } from "@/core/components/Field";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";
import Card from "@mui/material/Card";
import { Box } from "@mui/material";
import getFullAlternativeRoute from "@/utils/getFullAlternativeRoute";
import { useEffect, useRef } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import FlightPathBanner from "@/components/partials/FlightPathBanner";
import { Theme } from "@mui/system";
import fields, {
  FIELD_KEY,
  FieldName,
  TRANSPORT_ORDER_PLACEHOLDER,
} from "@/components/custom/AlternativeTransportation/Transportations/ManualTransports/ManualTransportInput/fields";
import transportComponents from "@/components/custom/AlternativeTransportation/transportComponents";
import {
  AlternativeRouteComponent,
  DateTimeValidationType,
  FlightSearchResult,
  SelectedTransport,
  TransportRef,
  TransportType,
} from "@/core/types/types";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { useDefaultValue } from "@/core/hooks/useDefaultValue/useDefaultValue";

type FormValues = {
  transportsPlaceholders: AlternativeRouteComponent[];
  fieldKey: string;
};

interface DynamicInputVariables {
  origin: string;
  destination: string;
  stopovers: string;
}

const styles = (theme: Theme) => ({
  cardDivider: {
    height: "2px",
    backgroundColor: theme.palette.secondary.light,
    width: "100%",
  },
});

const FIELD_NAME = "transportsPlaceholders";
const DEFAULT_TRANSPORTATION_MODE = TransportType.plane;
const DYNAMIC_INPUT_VARIABLES = ["origin", "destination", "stopovers"];

const AlternativeTransportations = ({ field }: FieldProps) => {
  const {
    properties: { requiredInputVariables, disableFutureDates },
    defaultValue,
  } = field;
  const classes: any = useCustomStyles(styles);

  if (!requiredInputVariables) {
    throw new Error("Missing required input variable names.");
  }

  const { result, loading } = useGetPreviousAnswers(requiredInputVariables, {
    enableCaching: false,
  });
  const { control, setValue } = useFormContext<FormValues>();
  const { fields: transportsPlaceholders, replace } = useFieldArray({
    control,
    name: FIELD_NAME,
  });
  const inputTransports = useDefaultValue(defaultValue);
  const transportRefs = useRef<(TransportRef | null)[]>([]);

  function findDynamicInputVariables(
    requiredVariables: string
  ): DynamicInputVariables {
    const inputVariablesAsArray = requiredVariables.split(",");

    return DYNAMIC_INPUT_VARIABLES.map((airportKey) =>
      airportKey.toLowerCase()
    ).reduce((acc: any, dynamicVariableName: string) => {
      const inputVariableName = inputVariablesAsArray.find((item) =>
        item.toLowerCase().includes(dynamicVariableName)
      );
      if (!inputVariableName)
        throw Error(`Missing ${dynamicVariableName} input variable`);

      acc[dynamicVariableName] = inputVariableName;
      return acc;
    }, {});
  }

  const dynamicInputVariables = findDynamicInputVariables(
    requiredInputVariables
  );
  const {
    departureDate,
    [dynamicInputVariables.origin]: originAirportData,
    [dynamicInputVariables.destination]: destinationAirportData,
    transportationMode,
    [dynamicInputVariables.stopovers]: stopoverData,
  } = result;
  const variablesDataIsValid = Object.keys(result).length > 0;

  useEffect(() => {
    if (!loading) {
      const alternativeRoute: AlternativeRouteComponent[] =
        getFullAlternativeRoute(
          originAirportData,
          destinationAirportData,
          transportationMode ?? DEFAULT_TRANSPORTATION_MODE,
          stopoverData
        );

      replace(alternativeRoute);
      setValue("fieldKey", field.key);
    }
  }, [loading]);

  const renderGenericTransportField = (
    routeComponent: AlternativeRouteComponent,
    index: number
  ) => {
    const altTransportField = fields[FieldName.GenericAlternativeTransport];
    const fieldKey = altTransportField.key
      .replace(TRANSPORT_ORDER_PLACEHOLDER, index.toString())
      .replace(FIELD_KEY, field.key);
    const allowedLegDepartureTime: string | undefined =
      index === 0
        ? field.properties?.[DateTimeValidationType.After]
        : undefined;
    const defaultTransport = inputTransports?.find(
      (transport: FlightSearchResult | SelectedTransport) =>
        transport.departureAirportIata === routeComponent.departure.iata &&
        transport.arrivalAirportIata === routeComponent.arrival.iata &&
        ((transport as SelectedTransport).airlineIata
          ? routeComponent.transportationMode === TransportType.plane
          : routeComponent.transportationMode !== TransportType.plane)
    );

    const genericTransportField = {
      ...altTransportField,
      id: fieldKey,
      key: fieldKey,
      departureDate,
      destinationAirportData: routeComponent.arrival,
      originAirportData: routeComponent.departure,
      transportOrder: index,
      transportType: routeComponent.transportationMode,
      transportsFieldKey: field.key,
      disableFutureDates,
      defaultTransport,
      properties: {
        ...altTransportField.properties,
        [DateTimeValidationType.After]: allowedLegDepartureTime,
      },
      onDateUpdate: (newDate: string) => {
        const nextTransport = transportRefs.current[index + 1];
        if (nextTransport) {
          nextTransport.updateDate(newDate);
        }
      },
    };

    const GenericTransport =
      transportComponents[routeComponent.transportationMode];
    return (
      <GenericTransport
        field={genericTransportField}
        ref={(el: TransportRef) => (transportRefs.current[index] = el)}
      />
    );
  };

  return (
    <>
      {transportsPlaceholders.map(
        (
          routeComponent: { id: string } & AlternativeRouteComponent,
          index: number
        ) => {
          if (!variablesDataIsValid) return null;

          // evaluating a stopover route order
          const departureStopover =
            routeComponent.departure.iata !== originAirportData.iata
              ? index.toString()
              : undefined;
          const arrivalStopover =
            routeComponent.arrival.iata !== destinationAirportData.iata
              ? (index + 1).toString()
              : undefined;

          return (
            <Box key={index || routeComponent.id}>
              <Card sx={{ boxShadow: "none", padding: 2 }}>
                <FlightPathBanner
                  originAirportData={routeComponent.departure}
                  destinationAirportData={routeComponent.arrival}
                  departureStopoverOrder={departureStopover}
                  arrivalStopoverOrder={arrivalStopover}
                />
              </Card>
              <Box sx={classes?.cardDivider}></Box>
              {renderGenericTransportField(routeComponent, index)}

              {transportsPlaceholders.length - index - 1 !== 0 && (
                <Box sx={{ height: 2, mb: 3 }}></Box>
              )}
            </Box>
          );
        }
      )}
    </>
  );
};

export default AlternativeTransportations;
